import { DatePickerWrapper, MainWrapper, MembersWrapper, MenuWrapper, TextWrapper } from './calendar-page-navigation.style'
import { Avatar, Button, DatePicker, Image, Select, Typography, theme } from 'antd'
import { LoadingOutlined, ReloadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons'
import { useTranslate , addDays, getDateRange, min, queryClient }  from 'old/utils'
import { useConfig, useTeamMembers } from 'old/api'
import dayjs from 'dayjs'
import { HorizontalContainer, Loading, NotFound, PageError } from 'old/components/styled'
import { Shop, TeamMember }  from 'old/types'
import { useEffect, useRef, useState } from 'react'
import { CALENDAR_RESOURCE_LSK, DEBOUNCING_TIMEOUT } from 'app.constants'
const { Text } = Typography

type Props = {
    shop: Shop,
    startDate: Date
    numberOfDays: number
    selectedTeamMembers: TeamMember[] | undefined
    areSlotsLoading: boolean
    onStartDateChange: (date: Date) => void
    onMembersChange: (teamMembers: TeamMember[] | undefined) => void
}

export const CalendarPageNavigation = ({
    shop,
    startDate,
    numberOfDays,
    selectedTeamMembers,
    areSlotsLoading,
    onStartDateChange,
    onMembersChange
}: Props) => {

    //hooks:
    const localStorageIds = getTeamMembersFromLocalStorage(shop.id)
    const { token: { fontSizeSM, fontSizeLG, marginXXS } } = theme.useToken()
    const { __ } = useTranslate()
    const { data: config, isLoading: isConfigLoading } = useConfig()

    //ref:
    const container = useRef<HTMLDivElement>(null)

    //debounce timeout:
    const lastKeyPressTimeoutId = useRef(0 as number | NodeJS.Timeout)

    //state:
    const [ isRefreshSelected, setIsRefreshSelected ] = useState(false)
    const [ search, setSearch ] = useState(null as string | null)
    const [ isTyping, setIsTyping ] = useState(false)

    //teamMembers:
    const { data: teamMembers, isLoading: isMembersLoading } = useTeamMembers(search, 50)

    //select team members:
    useEffect(() => {
        if(teamMembers){
            if(teamMembers.length){
                if(localStorageIds.length > 0){
                    onMembersChange(teamMembers.filter(member => localStorageIds.includes(member.id)))
                }else if(container.current){
                    const containerWidth = container.current.offsetWidth
                    const columnsNumber = min(Math.floor(containerWidth / 150), teamMembers.length)
                    const selectedTM = []
                    for(let i=0; i < columnsNumber; i++){
                        selectedTM.push(teamMembers[i])
                    }
                    onMembersChange(selectedTM)
                }
            }else{
                onMembersChange([])
            }
        }
    }, [isMembersLoading, shop])

    // disable refresh button loading for other fetches:
    useEffect(() => { !areSlotsLoading && setIsRefreshSelected(false) }, [areSlotsLoading])

    //handleSearch:
    const handleSearch = (search: string) => {
        setIsTyping(true)
        clearTimeout(lastKeyPressTimeoutId.current)
        if(search.length < 1){
            setSearch(null)
            setIsTyping(false)
        }else{
            lastKeyPressTimeoutId.current = setTimeout(() => {
                setSearch(search)
                setIsTyping(false)
            }, DEBOUNCING_TIMEOUT)
        }
    }

    //updateSelectedMembers:
    const updateSelectedMembers = (shop: Shop, newValue: string[]) => {
        if(teamMembers){
            
            // find the id list:
            const selectedMemberIds = newValue.map(val => val.split('__')[0])
            
            // convert the id list to the object list:
            const selectedTMs: TeamMember[] = []
            selectedMemberIds.map(selectedId => {
                const selectedTeamMember = teamMembers.find(member => member.id === selectedId)
                if(selectedTeamMember){
                    selectedTMs.push(selectedTeamMember)
                }
            })

            // update the selected list:
            onMembersChange(selectedTMs)

            // update the local storage:
            saveTeamMembersToLocalStorage(shop.id, selectedMemberIds)

        }
    }
    
    //render:
    return (
        (isConfigLoading || !config) ? null :
        <MainWrapper ref={container}>
            
            <TextWrapper>

                {/* date */}
                <Text style={{ fontSize: fontSizeLG, fontFamily: 'inter-medium' }}>
                    {getDateRange(startDate, numberOfDays, config, true)}
                </Text>
            
            </TextWrapper>

            <MenuWrapper>

                <DatePickerWrapper>

                    {/* date picker */}
                    <DatePicker
                        inputReadOnly
                        defaultValue={dayjs(startDate)}
                        value={dayjs(startDate)}
                        allowClear={false}
                        style={{ width: '100%', height: 'fit-content' }}
                        onChange={val => onStartDateChange(dayjs(val).toDate())}
                    />

                    <HorizontalContainer>

                        {/* left arrow */}
                        <Button
                            icon={<LeftOutlined style={{ fontSize: fontSizeSM }}/>}
                            title={__`previous_day`}
                            size={'middle'}
                            style={{ fontSize: fontSizeSM, backgroundColor: 'transparent' }}
                            onClick={() => onStartDateChange(addDays(startDate, -1))}
                        />
                        
                        {/* right arrow */}
                        <Button
                            icon={<RightOutlined style={{ fontSize: fontSizeSM }}/>}
                            title={__`next_day`}
                            size={'middle'}
                            style={{ fontSize: fontSizeSM, backgroundColor: 'transparent' }}
                            onClick={() => onStartDateChange(addDays(startDate, 1))}
                        />

                        {/* refresh */}
                        <Button
                            icon={(areSlotsLoading && isRefreshSelected) ? <LoadingOutlined style={{ fontSize: fontSizeSM }}/> : <ReloadOutlined style={{ fontSize: fontSizeSM }}/>}
                            title={__`refresh`}
                            size={'middle'}
                            style={{ fontSize: fontSizeSM, backgroundColor: 'transparent' }}
                            onClick={() => {
                                setIsRefreshSelected(true)
                                queryClient.invalidateQueries(['calendarSlots'])
                            }}
                        />
                        
                    </HorizontalContainer>

                </DatePickerWrapper>

                <MembersWrapper>

                    {/* team teamMembers */}
                    <Select
                        mode="multiple"
                        options={isMembersLoading ? [] : getMembersOptions(teamMembers ? teamMembers : [])}
                        value={(isMembersLoading || !teamMembers) ? undefined : getMembersValue(selectedTeamMembers, teamMembers)}
                        notFoundContent={
                            (isMembersLoading || isTyping) ? <Loading/> :
                            !teamMembers ? <PageError message={__`error`}/> :
                            <NotFound message={search ? __`not_found` : __`no_team_members`}/>
                        }
                        maxTagCount='responsive'
                        maxTagTextLength={4}
                        placeholder={__`select_team_teamMembers`}
                        style={{ width: '100%' }}
                        tagRender={props => (
                            <div style={{ marginRight: marginXXS }}>
                                {/* eslint-disable-next-line react/prop-types */}
                                {getMemberImage(teamMembers ? teamMembers : [], props.value)}
                            </div>
                        )}
                        onSearch={handleSearch}
                        onChange={newValue => updateSelectedMembers(shop, newValue)}
                    />

                </MembersWrapper>

            </MenuWrapper>

        </MainWrapper>
    )
    
}

//getMembersOptions:
const getMembersOptions = (teamMembers: TeamMember[]) => teamMembers.map(member => ({
    value: getMemberSelectorValue(member),
    label: member.name
}))

//getMembersValue:
const getMembersValue = (selectedTeamMembers: TeamMember[] | undefined, teamMembers: TeamMember[]) => {
    if(selectedTeamMembers){
        return selectedTeamMembers.map(selectedMember => {
            const member = teamMembers.find(member => member.id === selectedMember.id)
            return member ? getMemberSelectorValue(member) : ''
        })
    }else{
        return []
    }
}

//getMemberImage:
const getMemberImage = (teamMembers: TeamMember[], value: string) => {
    const member = teamMembers.find(member => getMemberSelectorValue(member) === value)
    return (
        <Avatar
            src={
                <Image
                    src={(member && member.image.path) ?? undefined}
                    preview={false}
                />
            }
            shape='circle'
            size='small'
            alt={member?.name}
        />
    )
}

//getMemberSelectorValue:
const getMemberSelectorValue = (member: TeamMember) => (
    `${member.id}__${member.name}__${member.email}__${member.mobile}__${member.description}` // to be able to search locally
)

type Storage = {
    shopId: string,
    teamMemberIds: string[]
}

//getTeamMembersFromLocalStorage:
const getTeamMembersFromLocalStorage = (shopId: string) => {
    const storageList = getAllShopsTeamMembersFromLocalStorage()
    const storage = storageList.find(st => st.shopId === shopId)
    return storage?.teamMemberIds ?? []
}

//getTeamMembersFromLocalStorage:
const getAllShopsTeamMembersFromLocalStorage = () => {
    let storageList: Storage[] = []
    try{
        const storageListString = localStorage.getItem(CALENDAR_RESOURCE_LSK)
        if(storageListString){
            storageList = JSON.parse(storageListString)
        }
    }catch(e){e}
    return storageList
}

//saveTeamMembersToLocalStorage:
const saveTeamMembersToLocalStorage = (shopId: string, teamMemberIds: string[]) => {
    const storageList = getAllShopsTeamMembersFromLocalStorage()
    let newStorageList = [...storageList]
    const isShopExists = storageList.find(st => st.shopId === shopId)
    if(isShopExists){
        newStorageList = storageList.map(st => {
            if(st.shopId === shopId){
                return { ...st, teamMemberIds }
            }else{
                return st
            }
        })
    }else{
        newStorageList.push({ shopId, teamMemberIds })
    }
    try{
        localStorage.setItem(CALENDAR_RESOURCE_LSK, JSON.stringify(newStorageList))
    }catch(e){e}
}
